import { useState } from "react";
import { useApp } from "@/context/AppContext";
import { ColorSwatch } from "@/components/ColorPicker";
import { Icon, ICON_NAMES } from "@/lib/icons";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Plus, Trash2, Check } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

export function CategoriesPage() {
  const { categories, categoriesApi, events } = useApp();
  const [editing, setEditing] = useState(null);

  const startNew = () => setEditing({ name: "", color: "#3B82F6", icon: "Tag" });
  const save = async () => {
    if (!editing.name.trim()) return;
    if (editing.id) await categoriesApi.update(editing.id, editing);
    else await categoriesApi.add(editing);
    setEditing(null);
  };
  const del = async (id) => { await categoriesApi.remove(id); if (editing?.id === id) setEditing(null); };
  const countFor = (id) => events.filter((e) => e.category_id === id).length;

  return (
    <div className="h-full overflow-y-auto p-4 lg:p-6">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="font-display text-2xl font-extrabold" style={{ color: "var(--text-primary)" }}>Categories</h2>
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>Organize events with custom colors & icons</p>
        </div>
        <button onClick={startNew} className="h-10 px-4 rounded-xl flex items-center gap-2 text-sm font-semibold text-white" style={{ background: "var(--accent-primary)" }} data-testid="button-new-category"><Plus size={18} /> New Category</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 max-w-4xl">
        <AnimatePresence>
          {categories.map((c) => (
            <motion.div key={c.id} layout initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, scale: 0.9 }}
              className="group flex items-center gap-3 p-4 rounded-2xl cursor-pointer" style={{ background: "var(--bg-card)", border: "1px solid var(--border-subtle)", borderLeft: `4px solid ${c.color}` }}
              onClick={() => setEditing(c)} data-testid={`category-card-${c.id}`}>
              <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `color-mix(in srgb, ${c.color} 24%, transparent)` }}>
                <Icon name={c.icon} size={20} style={{ color: c.color }} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-sm truncate" style={{ color: "var(--text-primary)" }}>{c.name}</p>
                <p className="text-xs" style={{ color: "var(--text-muted)" }}>{countFor(c.id)} events</p>
              </div>
              <button onClick={(e) => { e.stopPropagation(); del(c.id); }} className="opacity-0 group-hover:opacity-100 p-1.5 rounded-lg text-red-400 hover:bg-red-500/10"><Trash2 size={16} /></button>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      {editing && (
        <>
          <div className="fixed inset-0 z-40 bg-black/40" onClick={() => setEditing(null)} />
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
            className="fixed z-50 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[90vw] max-w-md rounded-3xl p-5 space-y-4"
            style={{ background: "var(--bg-sidebar)", border: "1px solid var(--border-subtle)" }} data-testid="category-editor">
            <h3 className="font-display text-xl font-bold" style={{ color: "var(--text-primary)" }}>{editing.id ? "Edit Category" : "New Category"}</h3>
            <Input value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} placeholder="Category name" autoFocus
              className="h-11 rounded-xl" style={{ background: "var(--bg-timeline-track)", borderColor: "var(--border-subtle)", color: "var(--text-primary)" }} data-testid="category-name-input" />
            <div>
              <label className="text-[11px] uppercase tracking-wider mb-1.5 block" style={{ color: "var(--text-muted)" }}>Color</label>
              <ColorSwatch color={editing.color} onChange={(v) => setEditing({ ...editing, color: v })} testId="category-color-swatch" />
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider mb-1.5 block" style={{ color: "var(--text-muted)" }}>Icon</label>
              <div className="grid grid-cols-8 gap-1.5 max-h-40 overflow-y-auto p-1">
                {ICON_NAMES.map((n) => (
                  <button key={n} onClick={() => setEditing({ ...editing, icon: n })} className="aspect-square rounded-lg flex items-center justify-center transition-all"
                    style={{ background: editing.icon === n ? editing.color : "var(--bg-timeline-track)", color: editing.icon === n ? "#fff" : "var(--text-secondary)" }}>
                    <Icon name={n} size={16} />
                  </button>
                ))}
              </div>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="ghost" onClick={() => setEditing(null)} style={{ color: "var(--text-secondary)" }}>Cancel</Button>
              <Button onClick={save} className="text-white font-semibold px-6" style={{ background: "var(--accent-primary)" }} data-testid="category-save-btn"><Check size={16} /> Save</Button>
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
}
